import { GamesServiceAPI } from '@API/data-services/games-service'
import { RootState } from '@AppState/redux-store'
import { ResponseError } from '@Generated/back-office-client/src'
import { useQuery } from '@tanstack/react-query'
import { orderBy } from 'lodash'
import { useSelector } from 'react-redux'
import { EditableGame } from './types'
import { editableGamesQueryKey } from './utils'

/**
 * The games the list and a single game page both read, in the order the playslip picker shows them.
 * There is no by-id read for a game, so the page picks its game out of the same cached list.
 */
export const useEditableGames = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)

  return useQuery({
    queryKey: editableGamesQueryKey(activeCustomerId),
    refetchOnMount: 'always',
    queryFn: async () => {
      const allGames = await GamesServiceAPI.getAllGames()
      // Only draw and rapid games are editable; listing the other families would offer edits the
      // endpoint rejects.
      const editable: EditableGame[] = [
        ...(allGames.drawGames ?? []),
        ...(allGames.rapidGames ?? []),
      ]

      // Matches the picker's own sort, down to the tiebreak: a game with no position is listed
      // after those that have one, and unplaced games fall back to id order.
      return orderBy(editable, [
        (game) => game.displayOrder ?? Number.MAX_SAFE_INTEGER,
        (game) => game.id,
      ])
    },
    // A 4xx answers the same way every time, so retrying only delays the load error.
    retry: (failureCount, error) => {
      if (error instanceof ResponseError && error.response.status < 500) {
        return false
      }

      return failureCount < 1
    },
  })
}
